/** Kucuk, bagimsiz sunum bilesenleri. */

import type { ReactNode } from 'react'
import { signed } from './rollStore'

export function Panel({
  title,
  children,
  className = '',
  right,
}: {
  title?: string
  children: ReactNode
  className?: string
  right?: ReactNode
}) {
  return (
    <section className={`panel overflow-hidden ${className}`}>
      {title && (
        <header className="panel-title flex items-center justify-between">
          <span>{title}</span>
          {right}
        </header>
      )}
      <div className="p-3">{children}</div>
    </section>
  )
}

export function StatBox({
  label,
  value,
  hint,
  onClick,
  tone = 'default',
  size = 'md',
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  onClick?: () => void
  tone?: 'default' | 'gold' | 'blood' | 'vital' | 'arcane'
  size?: 'sm' | 'md' | 'lg'
}) {
  const tones: Record<string, string> = {
    default: 'text-ink',
    gold: 'text-gold-bright',
    blood: 'text-blood',
    vital: 'text-vital',
    arcane: 'text-arcane',
  }
  const sizes: Record<string, string> = {
    sm: 'text-base px-1.5 py-1',
    md: 'text-xl px-2 py-1.5',
    lg: 'text-3xl px-2 py-2',
  }

  const content = (
    <>
      <div className="text-[0.6rem] font-semibold tracking-[0.16em] text-ink-faint uppercase">
        {label}
      </div>
      <div
        className={`tnum font-display font-semibold leading-tight ${sizes[size]} ${tones[tone]}`}
      >
        {value}
      </div>
      {hint && <div className="text-[0.65rem] text-ink-faint tnum">{hint}</div>}
    </>
  )

  if (!onClick) {
    return (
      <div className="flex flex-col items-center justify-center rounded border border-line-soft bg-surface-2 text-center">
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title="Zar atmak için tıkla"
      className="flex cursor-pointer flex-col items-center justify-center rounded border border-line-soft bg-surface-2 text-center transition-colors hover:border-gold-dim hover:bg-surface-3 active:scale-[0.97]"
    >
      {content}
    </button>
  )
}

/** Tıklanınca d20 atan rozet. Sheet'teki her sayı budur. */
export function RollBadge({
  value,
  onClick,
  tone = 'default',
  title,
  locked = false,
}: {
  value: number | string
  onClick?: () => void
  tone?: 'default' | 'gold' | 'muted'
  title?: string
  locked?: boolean
}) {
  const tones: Record<string, string> = {
    default: 'text-gold-bright',
    gold: 'text-gold-bright',
    muted: 'text-ink-faint',
  }
  const shown = typeof value === 'number' ? signed(value) : value

  // Tıklanabilir olduğunda gerçek <button> kullanıyoruz: klavye erişimi
  // ve ekran okuyucu uyumu bedavaya geliyor, CSS sınıfı aynı kalıyor.
  if (!onClick || locked) {
    return (
      <span
        className={`roll-badge tnum inline-block font-semibold ${tones[tone]}`}
        data-locked={locked}
      >
        {shown}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? 'Zar atmak için tıkla'}
      className={`roll-badge tnum inline-block cursor-pointer font-semibold ${tones[tone]}`}
    >
      {shown}
    </button>
  )
}

export function Row({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`flex items-center justify-between gap-2 py-1 ${className}`}>{children}</div>
  )
}

export function Tag({ children, tone = 'default' }: { children: ReactNode; tone?: string }) {
  const tones: Record<string, string> = {
    default: 'border-line-soft bg-surface-2 text-ink-muted',
    gold: 'border-gold-dim/60 bg-gold/10 text-gold',
    arcane: 'border-arcane/40 bg-arcane/10 text-arcane',
    vital: 'border-vital/40 bg-vital/10 text-vital',
  }
  return (
    <span
      className={`inline-block rounded border px-1.5 py-0.5 text-[0.65rem] ${tones[tone] ?? tones.default}`}
    >
      {children}
    </span>
  )
}
